import { _ } from "libs";

import React, { useContext, useEffect, useState } from "react";
import { openPlayerPopup, addWaitingReservation, checkWaitingReservation } from "../../../actions";
import { Link } from "react-router-dom";
import moment from "moment-timezone";
import ReservationPageContext from "./reservationPageContext";
import CourtItemMedium from "../../items/court/medium";
import PlayerItemSmall from "../../items/player/small";
import { SlotType, Player, BookingOwner, Slot, Booking, Profile } from "../../../types";
import { formatName } from "../../../libs/formatName";
import { AsyncButton } from "components";
import UserContext from "../../../contexts/userContext";

interface Props {
	onWaitingList: boolean;
}

const BookingDetail = ({ booking, profile }: { booking: Booking; profile: Profile }) => {
	const partners: Player[] = [];
	let owner: Player;

	let meOnBooking = false;

	const [{ reservationChecked, onWaitingList }, enableWaitingListButton] = useState({
		reservationChecked: false,
		onWaitingList: false,
	});

	useEffect(() => {
		enableWaitingListButton({
			reservationChecked: false,
			onWaitingList: false,
		});
	}, [booking.reservationId]);

	useEffect(() => {
		if (reservationChecked) {
			return;
		}
		checkWaitingReservation(booking.reservationId).then(({ onWaitingList }) => {
			enableWaitingListButton({
				reservationChecked: true,
				onWaitingList,
			});
		});
	}, [reservationChecked, booking.reservationId]);

	booking.players.forEach((player) => {
		if (player.playerId === profile.userId) {
			meOnBooking = true;
		}
		if (player.playerId !== booking.ownerId) {
			partners.push(player);
		} else {
			owner = player;
		}
	});

	if (owner) {
		if (owner.playerId === profile.userId) {
			meOnBooking = true;
		}
	}
	return (
		<>
			<h6>{booking.players.length > 0 ? _("bookingWizard.players.heading") : _("bookingWizard.info.heading")}</h6>
			<div>
				<div className="table defaultSpacing" style={{ marginBottom: "24px" }}>
					{owner && booking.owner !== BookingOwner.Club ? (
						<a onClick={openPlayerPopup.bind(null, owner.playerId)} className="cell">
							<PlayerItemSmall
								name={
									booking.owner === BookingOwner.Me
										? _("me")
										: owner.fullName
										? owner.fullName
										: formatName(owner.firstName, owner.lastName)
								}
								photoUrl={owner.photoUrl}
							/>
						</a>
					) : (
						booking.info
					)}
					{partners.map((player, key) => (
						<a onClick={openPlayerPopup.bind(null, player.playerId)} className="cell" key={key}>
							<PlayerItemSmall
								name={player.fullName ? player.fullName : formatName(player.firstName, player.lastName)}
								photoUrl={player.photoUrl}
								key={key}
							/>
						</a>
					))}
				</div>
				{booking.owner === BookingOwner.Me ? (
					<Link to={`/reservations/active/${booking.reservationId}`} className="button fullWidth">
						{_("bookingWizard.viewReservationLink")}
					</Link>
				) : (
					reservationChecked &&
					!onWaitingList &&
					!meOnBooking && (
						<AsyncButton
							className="button fullWidth"
							onClick={addWaitingReservation.bind(null, booking.reservationId)}
							callback={enableWaitingListButton.bind(null, {
								reservationChecked: true,
								onWaitingList: true,
							})}
							waitForFinish
						>
							{_("bookingWizard.addToWaitingList")}
						</AsyncButton>
					)
				)}
				{reservationChecked && onWaitingList && !meOnBooking && (
					<>
						<div className="msg info" style={{ marginBottom: "16px" }}>
							{_("bookingWizard.waitingListConfirmation")}
						</div>
						<Link to="/reservations/waiting-list" className="button light fullWidth">
							{_("bookingWizard.savedInTheWaitingList")}
						</Link>
					</>
				)}
			</div>
		</>
	);
};

const UnavailableReason = ({ unavailableSlot, profile }: { unavailableSlot: Slot; profile: Profile }) => {
	if (unavailableSlot.slotType === SlotType.NotAvailable) {
		return (
			<div className="group">
				<h6>{_("bookingWizard.info.heading")}</h6>
				<p>{unavailableSlot.slotContext.notAvailable.reason}</p>
			</div>
		);
	}

	if (unavailableSlot.slotType === SlotType.Blocked) {
		const blocking = unavailableSlot.slotContext.blocking;
		return (
			<div className="group">
				<h6>{_("bookingWizard.info.heading")}</h6>
				{blocking.shortDescription && <p>{blocking.shortDescription}</p>}
				{blocking.longDescription && <p>{blocking.longDescription}</p>}
			</div>
		);
	}

	if (unavailableSlot.slotType === SlotType.Booked) {
		return (
			<div className="group">
				<BookingDetail booking={unavailableSlot.slotContext.booking} profile={profile} />
			</div>
		);
	}
};

const Unavailable = () => {
	const pageContext = useContext(ReservationPageContext);
	const {
		clearReservationContext,
		reservation: { court, unavailableSlot },
		clubInfo: { timeZone },
	} = pageContext;

	const { profile } = useContext(UserContext);

	let title = _("bookingWizard.unavailable.heading");

	if (unavailableSlot.slotType === SlotType.NotAvailable) {
	}

	if (unavailableSlot.slotType === SlotType.Blocked) {
		const blocking = unavailableSlot.slotContext.blocking;
		title = _(`bookingWizard.${blocking.blockingType}.heading`, null, {
			blockingType: blocking.blockingType,
			longDescription: blocking.longDescription,
			shortDescription: blocking.shortDescription,
		});
	}

	if (unavailableSlot.slotType === SlotType.Booked) {
		switch (unavailableSlot.slotContext.booking.owner) {
			case "mine":
				title = _("bookingWizard.myBooking.heading");
				break;

			case "buddy":
				title = _("bookingWizard.playerBooking.heading");
				break;

			case "player":
				title = _("bookingWizard.playerBooking.heading");
				break;

			case "club":
				title = _("bookingWizard.clubBooking.heading");
				break;
		}
	}

	return (
		<div className="createReservation wizard unavailableSlot">
			<div className="wizardHeader">
				<div className="close" onClick={clearReservationContext} />
				<div className="title">{title}</div>
			</div>
			<div className="wizardBody">
				<div className="bookingDate">
					{moment.unix(unavailableSlot.startDateTime).tz(timeZone).format("dddd, MMMM D")}
				</div>
				<div className="bookingTime">
					{moment.unix(unavailableSlot.startDateTime).tz(timeZone).format("H:mm")} –{" "}
					{moment.unix(unavailableSlot.endDateTime).tz(timeZone).format("H:mm")}
				</div>
				{court ? <CourtItemMedium court={court} extraClass="capsule" /> : null}
				<UnavailableReason {...{ unavailableSlot, profile }} />
			</div>
		</div>
	);
};

export default Unavailable;
